import React, { useState, useRef, useCallback, useEffect, useLayoutEffect } from 'react';
import { X, Plus, Clock } from 'lucide-react';
import debounce from 'lodash.debounce';
import { Issue, WikiSuggestion } from 'util/constants';
import InputField from 'ui/InputField';
import LoadingSkeleton from 'ui/LoadingSkeleton';
import EntityAvatar from 'components/Entities/EntityAvatar';
import Modal from 'ui/Modal';
import AddCustomIssueModal from 'components/Issues/AddCustomIssueModal';
import { getFilteredRecentIssues, recentIssueToSuggestion, addRecentIssue, RecentIssue, removeRecentIssue } from 'util/recentIssuesUtils';
import { useIssueSearch, Suggestion as IssueSearchSuggestion } from 'hooks/useIssueSearch';
import { useBodyScrollLock } from 'ui/useBodyScrollLock';

interface Props {
  onSelect: (suggestion: Suggestion) => void;
  onEntityChipClose?: () => void;
  onSearchChange?: () => void;
  placeholder?: string;
  excludeIssueIds?: number[] | any;
  inputType?: 'search' | 'add';
  className?: string;
  autoFocus?: boolean;
  fieldRequired?: boolean;
  validationMessage?: string;
  validationState?: 'none' | 'valid' | 'invalid';
  fieldValue?: string;
  excludeCompoundIssues?: boolean;
  prepopulateData?: Issue | Suggestion | null;
  fullScreen?: boolean;
}


export type Suggestion = IssueSearchSuggestion;

// Pagination constants
const INITIAL_RESULTS_LIMIT = 30;
const WIKI_PAGINATION_STEP = 20;
const SOCIETEA_PAGE_SIZE = 10;

const IssueSearch: React.FC<Props> = ({ 
  onSelect, 
  onSearchChange,
  placeholder = "Search issues...",
  excludeIssueIds = [],
  inputType = 'search',
  className = "",
  autoFocus = false,
  fieldRequired = false,
  fieldValue = '',
  validationMessage = '',
  validationState = 'none',
  excludeCompoundIssues = false,
  onEntityChipClose,
  prepopulateData = null,
  fullScreen = false
}) => {
  const [searchQuery, setSearchQuery] = useState(fieldValue || '');
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [issueSelected, setIssueSelected] = useState(false);
  const [selectedSuggestion, setSelectedSuggestion] = useState<Suggestion | null>(null);
  const [showFullScreenSearch, setShowFullScreenSearch] = useState(false);
  const [recentIssues, setRecentIssues] = useState<RecentIssue[]>([]);
  const [showCustomIssueModal, setShowCustomIssueModal] = useState(false);
  
  const searchInputRef = useRef<HTMLInputElement>(null);
  const suggestionsRef = useRef<HTMLUListElement>(null);
  useBodyScrollLock(showSuggestions);
  
  // Use the centralized issue search hook
  const {
    suggestions: searchSuggestions,
    isSearching,
    isLoadingMore,
    hasMoreResults,
    performSearch,
    loadMoreResults,
    resetPaginationState,
  } = useIssueSearch({
    excludeIssueIds,
    excludeCompoundIssues,
    initialResultsLimit: INITIAL_RESULTS_LIMIT,
    wikiPaginationStep: WIKI_PAGINATION_STEP,
    backendPageSize: SOCIETEA_PAGE_SIZE,
  });
  
  useLayoutEffect(() => {
    if (prepopulateData && prepopulateData.cname) {
      setSearchQuery(prepopulateData.cname);
      setIssueSelected(true);
      
      // Handle both Issue and Suggestion types
      const suggestion: Suggestion = 'type' in prepopulateData && (prepopulateData.type === 'wiki' || prepopulateData.type === 'issue')
        ? (prepopulateData as Suggestion)
        : {
            type: 'issue' as const,
            id: (prepopulateData as Issue).id,
            name: prepopulateData.name || prepopulateData.cname,
            cname: prepopulateData.cname,
            wikipedia_link: (prepopulateData as Issue).wikipedia_link || '',
            subject_properties: (prepopulateData as Issue).properties?.subject_properties
          };
      
      setSelectedSuggestion(suggestion);
    } else {
      setIssueSelected(false);
      setSelectedSuggestion(null);
      setSearchQuery('');
    }
  }, [prepopulateData]);

  // Load recent issues on component mount and when showSuggestions changes
  useEffect(() => {
    setRecentIssues(getFilteredRecentIssues(excludeCompoundIssues));
  }, [excludeCompoundIssues]);

  // Refresh recent issues when suggestions are shown (to get latest data)
  useEffect(() => {
    if (showSuggestions) {
      const recent = getFilteredRecentIssues(excludeCompoundIssues);
      setRecentIssues(recent);
    }
  }, [showSuggestions, excludeCompoundIssues]);

  // Handle scroll to detect when to load more results
  const handleScroll = useCallback((event: React.UIEvent<HTMLUListElement>) => {
    const { scrollTop, scrollHeight, clientHeight } = event.currentTarget;
    const isNearBottom = scrollTop + clientHeight >= scrollHeight - 100; // Load more when 100px from bottom
    
    if (isNearBottom && hasMoreResults && !isLoadingMore) {
      loadMoreResults();
    }
  }, [hasMoreResults, isLoadingMore, loadMoreResults]);

  // eslint-disable-next-line react-hooks/exhaustive-deps
  const debouncedSearch = useCallback(
    debounce((value: string) => {
      performSearch(value);
      setShowSuggestions(true);
    }, 420),
    [performSearch]
  );

  const handleSearchChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const value = event.target.value;
    setSearchQuery(value);
    setShowSuggestions(true);
    debouncedSearch(value);
    if (onSearchChange) {
      onSearchChange();
    }
  };

  const handleInputFocus = () => {
    if (inputType === 'search' && (fullScreen === false || fullScreen === undefined)) {
      setShowFullScreenSearch(true);
    } else {
      setShowSuggestions(true);
    }
  };

  const handleClearSearch = () => {
    setSearchQuery('');
    setShowSuggestions(false);
    resetPaginationState();
    searchInputRef.current?.blur();
  };

  const handleSelectSuggestion = (suggestion: Suggestion) => {
    onSelect(suggestion);
    setSelectedSuggestion(suggestion);
    setShowSuggestions(false);
    setShowFullScreenSearch(false);
    setSearchQuery(suggestion.name);
    setIssueSelected(true);
    
    // Add selected issue to recent issues list (only for existing issues, not wiki suggestions).
    // Pass the full suggestion (which is { ...Issue, type: 'issue' } for backend results)
    // so that addRecentIssue persists the complete properties including subject_properties.
    if (suggestion.type === 'issue') {
      try {
        addRecentIssue(suggestion as unknown as Issue);
        setRecentIssues(getFilteredRecentIssues(excludeCompoundIssues));
      } catch (error) {
        console.error('Error adding selected issue to recent list:', error);
      }
    }
  };

  const handleEntityChipClose = () => {
    setSelectedSuggestion(null);
    setIssueSelected(false);
    setSearchQuery('');
    fieldValue = '';
    resetPaginationState();
    if (onEntityChipClose) {
      onEntityChipClose();
    }
    document.getElementById(`issue-add-${placeholder}`)?.focus();
  };

  /**
   * Open the custom issue modal without closing any parent overlays.
   */
  const handleOpenCustomIssueModal = () => {
    setShowSuggestions(false);
    setShowFullScreenSearch(false);
    setShowCustomIssueModal(true);
  };

  /**
   * Normalize a newly created issue into a suggestion and select it.
   */
  const handleCustomIssueCreated = (issue: Issue) => {
    const suggestion: Suggestion = {
      type: "issue" as const,
      id: issue.id,
      name: issue.name || issue.cname,
      cname: issue.cname,
      wikipedia_link: issue.wikipedia_link || "",
      subject_properties: issue.properties?.subject_properties
    };
    handleSelectSuggestion(suggestion);
    setShowCustomIssueModal(false);
  };

  // Render search suggestions list
  const renderSuggestionsList = (isModal: boolean = false) => {
    const isShowingRecent = searchQuery.length === 0 && recentIssues.length > 0;
    const suggestions = isShowingRecent
      ? recentIssues.map(recentIssueToSuggestion)
      : searchSuggestions;
    
    const handleRemoveRecent = (issueId: number) => {
      try {
        removeRecentIssue(issueId);
        setRecentIssues(getFilteredRecentIssues(excludeCompoundIssues));
      } catch (err) {
        console.error('Error removing recent issue:', err);
      }
    };

    return (
      <ul
        ref={suggestionsRef}
        onScroll={handleScroll}
        className={`bg-eoe-bg dark:bg-eoe-dark-bg-modal shadow-lg
          ${isModal ? 
            'flex-1 overflow-y-auto h-full' :
            inputType === 'search' && !fullScreen ? 
              'border border-eoe-border-main dark:border-eoe-dark-border-main border-b-0 flex-shrink overflow-y-auto max-h-[calc(100dvh-72px)]' :
              'border border-eoe-border-main dark:border-eoe-dark-border-main border-b-0 flex-1 overflow-y-auto'
          }
        `}
      >
        {/* Helper text for empty modal state */}
        {(isModal || fullScreen || (inputType === 'add' && showSuggestions)) && searchQuery.length === 0 && (
          <>
            {recentIssues.length > 0 ? (
              <li className="p-2 text-center">
                <div className={`flex items-center justify-start px-2 gap-2 text-eoe-text-meta dark:text-eoe-dark-text-meta text-sm`}>
                  <Clock className="size-4" />
                  <span>Recent</span>
                </div>
              </li>
            ) : (
              <li className="px-2 py-2 text-center">
                <p className="text-eoe-text-meta dark:text-eoe-dark-text-meta text-sm">
                  Start typing to search for issues or add new ones from Wikipedia
                </p>
              </li>
            )}
          </>
        )}
        {isShowingRecent ? (
          recentIssues.map((recent, index) => {
            const suggestion = recentIssueToSuggestion(recent);
            return (
              <li
                key={`recent-${recent.id}-${index}`}
                className="group px-2 py-2 hover:bg-eoe-icon dark:hover:bg-eoe-dark-icon cursor-pointer text-eoe-text dark:text-eoe-dark-text flex justify-between items-center"
                onClick={() => handleSelectSuggestion(suggestion)}
              >
                <EntityAvatar
                  entity={recent.issue as Issue}
                  entityId={recent.id}
                  entityType="issue"
                  entityChip={true}
                  showCloseChip={true}
                  onEntityChipClose={() => handleRemoveRecent(recent.id)}
                />
              </li>
            );
          })
        ) : (
          suggestions.map((suggestion, index) => (
          <li
            key={`${suggestion.type}-${suggestion.type === 'issue' ? suggestion.id : suggestion.cname}-${index}`}
            className="group px-2 py-2 hover:bg-eoe-icon dark:hover:bg-eoe-dark-icon cursor-pointer text-eoe-text dark:text-eoe-dark-text flex justify-between items-center"
            onClick={() => handleSelectSuggestion(suggestion)}
          >
            {suggestion.type === 'issue' ? (
              <EntityAvatar
                entity={suggestion as unknown as Issue}
                entityType="issue"
                entityChip={true}
                showCloseChip={false}
              />
            ) : (
              <>
            <div className="flex items-center gap-1.5 px-1.5 py-1">
              <span className="w-8 h-8 flex-shrink-0">
                    <img src={(suggestion as WikiSuggestion).thumbnail && (suggestion as WikiSuggestion).thumbnail.trim() !== '' ? (suggestion as WikiSuggestion).thumbnail : 'https://upload.wikimedia.org/wikipedia/en/8/80/Wikipedia-logo-v2.svg'} alt="Issue avatar" className="w-full h-full rounded-full object-cover" />
              </span>
              <div className="flex flex-col min-w-0">
                    <span className="leading-tight line-clamp-1 text-sm italic">
                  {suggestion.name}
                </span>
                    <span className="leading-tight line-clamp-1 text-xs italic">
                        {(suggestion as WikiSuggestion).short_description || ''}
                </span>
              </div>
            </div>
                <div className="flex items-center space-x-0 rounded-full group-hover:bg-eoe-icon-hover dark:group-hover:bg-eoe-dark-icon-hover text-eoe-gray dark:text-eoe-dark-text flex-shrink-0"><img src='https://upload.wikimedia.org/wikipedia/en/8/80/Wikipedia-logo-v2.svg' alt='Wikipedia Logo' className="w-5 h-5 rounded-full text-eoe-gray dark:text-eoe-dark-text flex-shrink-0" /><Plus className="w-5 h-5 rounded-full text-eoe-gray dark:text-eoe-dark-text flex-shrink-0" />
                </div>
              </>
            )}
          </li>
          ))
        )}
        
        {/* Loading indicator for initial search */}
        {isSearching && (
          <li className="issue-search-loading-skeleton px-2 py-2">
            <LoadingSkeleton
              avatarSize='h-9 w-9'
              variant="entity-avatar"
              count={INITIAL_RESULTS_LIMIT}
              containerClassName="issue-search-loading-skeleton-list"
            />
          </li>
        )}
        
        {/* Loading indicator for pagination */}
        {isLoadingMore && (
          <li className="issue-search-pagination-skeleton px-2 py-2">
            <LoadingSkeleton
              avatarSize='h-9 w-9'
              variant="entity-avatar"
              count={SOCIETEA_PAGE_SIZE}
              containerClassName="issue-search-pagination-skeleton-list"
            />
          </li>
        )}
        
        {/* End of results indicator */}
        {!hasMoreResults && searchSuggestions.length > INITIAL_RESULTS_LIMIT && (
          <li className="px-3 py-2 text-center text-xs text-eoe-text-meta dark:text-eoe-dark-text-meta border-t border-eoe-border-main dark:border-eoe-dark-border-main">
            End of results
          </li>
        )}
      </ul>
    );
  };

  return (
    <div className={`relative ${className}`}>
      <div className="relative">
      {inputType === 'search' ? (
        <>
          <input
            type="text"
            value={searchQuery}
            onChange={handleSearchChange}
            onFocus={handleInputFocus}
            ref={searchInputRef}
            placeholder={placeholder}
            autoFocus={autoFocus}
            className="eoe-input w-full bg-gray-200 dark:bg-eoe-dark-input rounded-full px-2 py-2 pr-8"
          />
          {searchQuery.length > 0 && (
            <button
              type="button"
              onClick={handleClearSearch}
              className="absolute right-2 top-1/2 transform -translate-y-1/2 text-gray-500 hover:text-gray-700 dark:text-gray-300 dark:hover:text-gray-100 p-1"
            >
              <X className="w-4 h-4" />
            </button>
          )}
        </>
        ) : (
        <>
          {issueSelected ? (
            <span className={`input-container relative flex-1`}>
                <div
                  className={`
                      w-full min-h-[60px] px-3 pt-6 pb-1
                      eoe-input bg-gray-200 dark:bg-eoe-dark-input
                      rounded-2xl
                      text-gray-800 dark:text-eoe-dark-text
                      transition-colors
                      resize-none
                  `}
                >
                <span 
                    className={`
                        absolute left-3
                        top-2 text-xs
                        transition-all
                        text-eoe-text-meta dark:text-eoe-dark-text-meta
                    `}
                >
                    {placeholder}
                    {fieldRequired && <span className="text-warning-red dark:text-dark-warning-red ml-[0.5px]">*</span>}
                </span>
                <EntityAvatar
                  entity={selectedSuggestion as unknown as Issue}
                  entityType="issue"
                  entityChip={true}
                  showCloseChip={true}
                  onEntityChipClose={handleEntityChipClose}
                />
                </div>
              </span>
          ) : (
            <InputField
              id={`issue-add-${placeholder}`}
              type="text"
              name={`issue-add-${placeholder}`}
              labelText={placeholder}
              fieldRequired={fieldRequired}
              value={searchQuery || fieldValue}
              onChange={handleSearchChange}
              onFocus={() => setShowSuggestions(true)}
              autoComplete="off"
              ref={searchInputRef}
              autoFocus={prepopulateData ? true : autoFocus}
              validationState={validationState}
              error={validationMessage}
            />
          )}
        </>
      )}
      </div>

      {/* Inline suggestions for 'add' input type or when fullScreen is true */}
      {(
        (showSuggestions && (searchSuggestions.length > 0 || isLoadingMore || isSearching)) || 
        (fullScreen && searchQuery.length === 0) || (showSuggestions && searchQuery.length === 0 && recentIssues.length > 0)
      ) && inputType === 'add' && (
      <div
        className={`search-suggestions-container flex flex-col bg-eoe-bg dark:bg-eoe-dark-bg-modal relative bottom-2 top-1 max-h-[200px] overflow-y-auto`}
      >
        {renderSuggestionsList(false)}
        <div className="flex-shrink-0 border-none"> 
          <button
            type="button"
            className="custom-issue-create-button hidden w-full bg-eoe-bg dark:bg-eoe-dark-bg-modal btn-secondary border border-eoe-border-main dark:border-eoe-dark-border-main border-b-0 py-[5px] px-2 rounded-none"
            onClick={handleOpenCustomIssueModal}
          >
            <span className="text-eoe-dark-text">Not on Wikipedia? Add custom</span>
          </button>
          <button className="w-full btn-secondary py-[5px] px-2 rounded-tr-none rounded-tl-none" onClick={() => setShowSuggestions(false)}>
            <span className="text-eoe-dark-text">Close search</span>
          </button>
        </div>
      </div>
      )}
      
      {/* Full-screen suggestions for fullScreen prop */}
      {((showSuggestions && (searchSuggestions.length > 0 || isLoadingMore || isSearching)) || (fullScreen && searchQuery.length === 0)) && fullScreen && (
      <div
        className="search-suggestions-container flex flex-col bg-eoe-bg dark:bg-eoe-dark-bg-modal fixed top-[60px] left-0 right-0 bottom-0 z-50 overflow-hidden"
      >
        {renderSuggestionsList(true)}
      </div>
      )}
      
      {/* Full-screen modal for search input when focused */}
      {showFullScreenSearch && (
        <Modal
          isOpen={showFullScreenSearch}
          onClose={() => setShowFullScreenSearch(false)}
          title=""
          displayStyle="cover"
          className="mobile-search-modal p-0 sm:p-0 max-w-full h-full sm:max-w-full sm:h-full sm:rounded-none overflow-hidden"
        >
          <div className="w-full flex flex-col">
            {/* Search Input at top */}
            <div className="left-2 my-2 mx-4 bg-eoe-bg dark:bg-eoe-dark-bg-modal relative z-10">
              <input
                type="text"
                value={searchQuery}
                onChange={handleSearchChange}
                placeholder={placeholder}
                autoFocus={true}
                className="eoe-input w-full bg-gray-200 dark:bg-eoe-dark-input rounded-full px-2 py-2 pr-8"
              />
              {searchQuery.length > 0 && (
                <button
                  type="button"
                  onClick={() => {
                    setSearchQuery('');
                    resetPaginationState();
                  }}
                  className="absolute right-2 top-1/2 transform -translate-y-1/2 text-gray-500 hover:text-gray-700 dark:text-gray-300 dark:hover:text-gray-100 p-1"
                >
                  <X className="w-4 h-4" />
                </button>
              )}
            </div>
            {/* Search results container */}
            <div className="flex-1 flex flex-col overflow-hidden">
              {renderSuggestionsList(true)}
            </div>
          </div>
        </Modal>
      )}

      <AddCustomIssueModal
        open={showCustomIssueModal}
        onClose={() => setShowCustomIssueModal(false)}
        onIssueCreated={handleCustomIssueCreated}
      />
    </div>
  );
};

export default IssueSearch;